<template>
  <hel-module title="企业经营核心指标" :class="scope.clsPrefix" v-loading="loading">
    <div class="indicators-container">
      <div class="indicators-main">
        <div
          class="indicator-item"
          v-for="(item, index) in indicators"
          :key="index"
          @mouseenter="hoverIndex = index"
          @mouseleave="hoverIndex = -1"
        >
          <div class="indicator-header">
            <span class="indicator-name">{{ item.name }}</span>
            <span :class="['indicator-trend', item.trendClass]">
              <span class="trend-value">{{ item.trendValue }}</span>
              <span class="trend-icon">{{ item.trendIcon }}</span>
            </span>
          </div>
          <div class="indicator-value">
            <span class="value-num">{{ item.value }}</span>
            <span class="value-unit">{{ item.unit }}</span>
          </div>
          <div class="indicator-mask" v-show="hoverIndex === index">
            <button class="smart-analysis-btn" @click.stop="handleSmartAnalysis(item)">
              智能分析
            </button>
          </div>
        </div>
      </div>
    </div>
  </hel-module>
</template>

<script>
import HelModule from './common/module/index.vue'
import axios from 'axios'
export default {
  name: 'BusinessIndicators',
  components: {
    HelModule
  },
  props: {
    data: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      loading: false,
      scope: {
        clsPrefix: 'rvc-cosmoplat-workspace-business-indicators'
      },
      hoverIndex: -1,
      indicators: [
        { name: '营业收入', value: '--', unit: '万元', trendValue: '--', trendClass: 'trend-up', trendIcon: '↑' },
        { name: '外单占比', value: '--', unit: '%', trendValue: '--', trendClass: 'trend-down', trendIcon: '↓' },
        { name: '签约额', value: '--', unit: '万元', trendValue: '--', trendClass: 'trend-up', trendIcon: '↑' },
        { name: '综合毛利率', value: '--', unit: '%', trendValue: '--', trendClass: 'trend-down', trendIcon: '↓' },
        { name: '管理利润', value: '--', unit: '万元', trendValue: '--', trendClass: 'trend-flat', trendIcon: '' },
        { name: '人效', value: '--', unit: '%', trendValue: '--', trendClass: 'trend-down', trendIcon: '↓' }
      ]
    }
  },
  created() {
    this.fetchCoreMetrics()
  },
  methods: {
    handleSmartAnalysis(item) {
      if (item.name === '综合毛利率') {
        this.$emit('smart-analysis', { ...item, forceRealChat: true })
      } else {
        this.$emit('smart-analysis', item)
      }
    },
    formatNumber(value) {
      if (value == null || isNaN(value)) return '--'
      return Number(value).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatPercent(value) {
      if (value == null || isNaN(value)) return '--'
      return Number(value).toFixed(1)
    },
    getTrend(value) {
      if (value == null || isNaN(value) || Number(value) === 0) {
        return { trendValue: '--', trendClass: 'trend-flat', trendIcon: '' }
      }
      const num = Number(value)
      if (num > 0) {
        return { trendValue: '+' + num.toFixed(1) + '%', trendClass: 'trend-up', trendIcon: '↑' }
      }
      return { trendValue: num.toFixed(1) + '%', trendClass: 'trend-down', trendIcon: '↓' }
    },
    async fetchCoreMetrics() {
      this.loading = true
      try {
        const year = new Date().getFullYear().toString()
        const response = await axios.post(
          'https://bigdata.cosmoplat.com/prod-api/system/api/call/cosmoCoreMetrics',
          { secret: '88py6X', paramList: [{ paramName: 'data_year', paramValue: year }] },
          { headers: { 'Content-Type': 'application/json' }}
        )
        const { data } = response.data
        if (data && data.length > 0) {
          const m = data[0]
          const map = [
            { name: '营业收入', key: 'operating_revenue', trendKey: 'operating_revenue_yoy_pct', unit: '万元', fmt: 'number' },
            { name: '外单占比', key: 'external_order_ratio_pct', trendKey: 'external_order_ratio_yoy_pp', unit: '%', fmt: 'percent' },
            { name: '签约额', key: 'signing_amount', trendKey: 'signing_yoy_pct', unit: '万元', fmt: 'number' },
            { name: '综合毛利率', key: 'gross_margin_pct', trendKey: 'gross_margin_yoy_pp', unit: '%', fmt: 'percent' },
            { name: '管理利润', key: 'management_profit', trendKey: 'management_profit_completion_pct', unit: '万元', fmt: 'number' },
            { name: '人效', key: 'people_efficiency', trendKey: 'people_efficiency_yoy_pct', unit: '%', fmt: 'percent' }
          ]
          this.indicators = map.map((f) => {
            const val = f.fmt === 'percent' ? this.formatPercent(m[f.key]) : this.formatNumber(m[f.key])
            let trend
            if (f.name === '管理利润') {
              const pct = m[f.trendKey]
              trend = { trendValue: pct != null ? '目标完成 ' + Number(pct).toFixed(1) + '%' : '--', trendClass: 'trend-flat', trendIcon: '' }
            } else {
              trend = this.getTrend(m[f.trendKey])
            }
            return { name: f.name, value: val, unit: f.unit, trendValue: trend.trendValue, trendClass: trend.trendClass, trendIcon: trend.trendIcon }
          })
        }
      } catch (error) {
        console.error('获取企业经营核心指标失败:', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$class-prefix: "rvc-cosmoplat-workspace-business-indicators";
.indicators-main {
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
  margin-bottom: 8px;
}
.#{$class-prefix} {
  overflow: hidden;
  .indicators-container {
    height: 100%;
    width: 100%;
    // overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: transparent;
  }
  .indicators-container:hover {
    scrollbar-width: thin;
    scrollbar-color: #b0b0b0 #e8e8e8;
  }
  .indicator-item {
    // flex: 0 0 auto;
    // width: 180px;
    flex: 1 ;
    min-width: 120px;
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #eff2fc;
    padding: 12px 16px;
    position: relative;
    .indicator-header {
      display: flex;
      flex-direction: column;
      gap: 2px;
      margin-bottom: 8px;
    }
    .indicator-name {
      font-size: 12px;
      font-family: PingFangSC-Medium;
      font-weight: 600;
      color: #333333;
    }
    .indicator-trend {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 12px;
      font-family: PingFangSC-Regular;
      font-weight: 400;
      &.trend-up {
        color: #73b950;
      }
      &.trend-down {
        color: #f44f4f;
      }
      &.trend-flat {
        color: #f4b13d;
      }
    }
    .trend-icon {
      font-size: 12px;
      font-weight: 900;
    }
    .trend-value {
      font-weight: 500;
    }
    .indicator-value {
      display: flex;
      align-items: baseline;
      gap: 8px;
    }
    .value-num {
      font-size: 24px;
      font-weight: 700;
      font-family: PingFangSC-Semibold;
      color: #333;
      line-height: 32px;
    }
    .value-unit {
      font-size: 12px;
      color: #333;
      font-weight: 400;
    }
    .indicator-mask {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #edf1fb3b;
      backdrop-filter: blur(1px);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .smart-analysis-btn {
      width: 64px;
      height: 24px;
      border: none;
      background-image: linear-gradient(270deg, #4a5ff3 0%, #4e86ff 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      font-family: PingFangSC-Regular;
      font-weight: 400;
      font-size: 12px;
      color: #ffffff;
      line-height: 12px;
    }
  }
}
@media screen and (max-width: 1440px) {
  .#{$class-prefix} {
    .indicator-item {
      .value-num {
        font-size: 18px;
      }
      .indicator-name {
        font-size: 11px;
      }
    }
  }
}
</style>

<style lang="scss">
.rvc-cosmoplat-workspace-business-indicators .indicators-container::-webkit-scrollbar {
  height: 0;
}
.rvc-cosmoplat-workspace-business-indicators
  .indicators-container:hover::-webkit-scrollbar {
  height: 6px;
}
.rvc-cosmoplat-workspace-business-indicators
  .indicators-container::-webkit-scrollbar-track {
  background: #e8e8e8;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-business-indicators
  .indicators-container::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border-radius: 3px;
}
.rvc-cosmoplat-workspace-business-indicators
  .indicators-container::-webkit-scrollbar-thumb:hover {
  background: #909090;
}
</style>
